import { Edges } from "@react-three/drei";
import * as THREE from "three";
import { useMemo } from "react";
import { fromMM } from "../Utils/frameUtils";
import {
  buildBaseFrameChannel,
  buildESPConeGeometry,
  buildHollowFrame,
} from "../Utils/geometryUtils";
import { useStore } from "../Store/zustandStore";

export default function BoxFan({ frame, selected }) {
  const [frameThickness] = useStore((state) => [fromMM(state.frameThickness)]);

  const frameLength = fromMM(frame.length);
  const frameHeight = fromMM(frame.height);
  const frameWidth = fromMM(frame.width);

  // BoxFan body dimensions
  const boxHeight = fromMM(frame.fan?.boxHeight ?? 900);
  const boxWidth = fromMM(frame.fan?.boxWidth ?? 1000);
  const boxLength = fromMM(frame.fan?.boxLength ?? 1000);
  const footSize = fromMM(100);
  const casingProfileWidth = fromMM(25);
  const casingCornerStep = casingProfileWidth / 2;
  const casingStepLineThickness = fromMM(1);

  // BOX-T-HT mounting bars, from the Fans table via frame.fan. The fan stands
  // on two steel bars that run ACROSS its width and stick out either side, so
  // the protruding ends can be bolted to the floor.
  //   mountingBarPitch       key A - bar to bar, ALONG the fan (world X)
  //   mountingBarHoleSpacing key B - hole to hole on one bar (world Z)
  //   mountingBarLength      key F - full length of each bar (world Z)
  // so hole spacing < bar length, both across the fan, while the pitch is the
  // one value measured along it.
  // Absent for any range with no mounting bars, in which case the four corner
  // feet are drawn as before.
  const mountingBarPitch       = fromMM(frame.fan?.mountingBarPitch ?? 0);
  const mountingBarHoleSpacing = fromMM(frame.fan?.mountingBarHoleSpacing ?? 0);
  const mountingBarLength      = fromMM(frame.fan?.mountingBarLength ?? 0);
  const hasBaseBar             = mountingBarPitch > 0 && mountingBarLength > 0;
  // Bar section and tab: not dimensioned in the selector, so these are drawn
  // to look right against the casing rather than claiming to be exact.
  const barHeight    = fromMM(70);   // selector key E - bar depth
  const barThickness = fromMM(80);   // bar width along the fan
  // Base frame: a perimeter channel that stands AROUND the mounting bars,
  // wrapping them from the side rather than sitting on top of them - the shape
  // in the client drawing. It spans the BOX width across the fan, sitting flush
  // under the casing, so the mounting bars are the only thing protruding either
  // side - they run the full extent (F) out to their floor-fixing bolt tabs.
  // Overall height of the channel, floor to box underside. Not yet dimensioned
  // in the selector, so it defaults to 70mm; the read from frame.fan is already
  // wired up for when the Fans table starts supplying it (see the commented-out
  // "Fan Baseframe" part lookup in MaicoUnit.cs, which resolves 50/75/100/125).
  const BASE_FRAME_HEIGHT_MM = 70;
  const baseFrameHeight = fromMM(frame.fan?.baseFrameHeight ?? BASE_FRAME_HEIGHT_MM);
  const channelThickness = fromMM(40);
  // Angled bottom corners: the channel stands widest at the top, under the box,
  // and is drawn in at the floor along both axes, so the front elevation shows
  // the same slanted corner as the side one - the wedge the client drawing
  // marks. Both insets are taken up over the full frame height.
  const channelTaperHeight = baseFrameHeight;
  const channelFootInset = baseFrameHeight * 0.4;
  const channelFootInsetZ = baseFrameHeight * 0.3;
  // Channel spans the mounting bars (key F), not the box, so its chamfered ends
  // ARE the outer corner of the base - the bend starts at the base's edge
  // instead of sitting inboard of the bar ends. The bars are drawn just short
  // of the channel's floor outline so they stay inside it at every height and
  // do not poke a square corner back through the taper.
  const baseFrameSpan = mountingBarLength > 0 ? mountingBarLength : boxWidth;
  // Bars end inside the channel cavity so they never cross its wall: the floor
  // outline is inset, so the bar has to clear that inset plus the wall itself.
  const barSpan = Math.max(
    mountingBarLength - (channelFootInsetZ + channelThickness) * 2 - fromMM(4),
    0,
  );
  const holeRadius   = fromMM(12);
  // Thin enough that the two silhouettes merge into one line on the sheet.
  const holeLineThickness = fromMM(1);
  // Fixing holes are dimensioned directly now: key A is hole-to-hole ON a bar,
  // so each hole sits half that either side of centre. Falls back to just inside
  // the bar's end when the table has no hole spacing, and is held inside the
  // bar either way now that the bar no longer overruns the channel.
  const holeZ = Math.min(
    mountingBarHoleSpacing > 0
      ? mountingBarHoleSpacing / 2
      : Math.max(barSpan / 2 - fromMM(25), 0),
    Math.max(barSpan / 2 - holeRadius * 1.5, 0),
  );
  const cornerOffset = fromMM(20);
  // Connector openings, from the Fans table via frame.fan (see MaicoUnit.cs).
  // The inlet the cone lands on is a square (InletHeight); the discharge
  // connector is OutletHeight x OutletWidth. Both are centred on their face.
  const inletSize = fromMM(frame.fan?.boxInletHeight ?? 404);
  const outletHeight = fromMM(frame.fan?.boxOutletHeight ?? 404);
  const outletWidth = fromMM(frame.fan?.boxOutletWidth ?? 404);
  const grillBarThickness = fromMM(2);
  const cutoutGrillBarWidth = fromMM(100);
  const frontBackGrillSpacing = fromMM(150);
  const frontBackGrillBarWidth = fromMM(500);
  const frontBackGrillBarLength = fromMM(50);
  // Louvres below the outlet on the discharge face - dashed line style in Front View
  // Short dash segments arranged horizontally with wider coverage area
  const louvreDashLength = fromMM(10); // short protrusion depth (along X)
  const louvreDashThickness = fromMM(4); // vertical thickness of each dash
  const louvreRowSpacing = fromMM(45); // vertical spacing between rows
  const louvreRows = 4; // 4 horizontal dashed lines below outlet
  const louvreDashesPerRow = 9; // dashes per row
  const louvreSpanWidth = boxWidth * 0.9; // extend beyond the outlet across the box face
  const louvreDashWidth = Math.min(
    fromMM(60),
    (louvreSpanWidth * 0.68) / louvreDashesPerRow,
  ); // fit nine segments within the wider louvre span
  const louvreBarPositions = [];
  // Start from near top of outlet opening and extend downward with more rows
  const louvreStartY = -outletHeight / 2 - louvreRowSpacing;
  for (let row = 0; row < louvreRows; row++) {
    // Spread dashes across the full outlet width for wider coverage
    const totalDashWidth = louvreDashesPerRow * louvreDashWidth;
    const totalGapWidth = louvreSpanWidth - totalDashWidth;
    const gapBetweenDashes = totalGapWidth / (louvreDashesPerRow + 1);
    for (let d = 0; d < louvreDashesPerRow; d++) {
      const zPos = -louvreSpanWidth / 2 + gapBetweenDashes + d * (louvreDashWidth + gapBetweenDashes) + louvreDashWidth / 2;
      louvreBarPositions.push([
        boxLength / 2 + louvreDashLength / 2 + 0.01,
        louvreStartY - row * louvreRowSpacing,
        zPos,
      ]);
    }
  }

  // Terminal box: the small wiring enclosure on the discharge face, shown in
  // the client drawing at the bottom-front of the box with a conduit curving
  // into the lower gap below it. Not dimensioned in the selector, so sized to
  // look right against the box (same convention as the bars and grills).
  const terminalBoxProtrusion = fromMM(120); // out of the discharge face (world X)
  const terminalBoxWidth      = fromMM(150); // along the box width (world Z)
  const terminalBoxHeight     = fromMM(150); // vertical (world Y)
  const terminalBoxEdgeInset  = fromMM(43); // leave clearance from the casing edge and inner box
  const terminalBoxBottomUp   = fromMM(80); // its underside above the box bottom
  const terminalBoxX = boxLength / 2 + terminalBoxProtrusion / 2;
  const terminalBoxY = -boxHeight / 2 + terminalBoxBottomUp + terminalBoxHeight / 2;
  const terminalBoxZ = boxWidth / 2 - terminalBoxEdgeInset - terminalBoxWidth / 2;
  // Conduit: a flexible droop, not a straight run - the client drawing shows
  // the pipe leaving the BOTTOM MIDDLE of the terminal box, sweeping toward
  // the box centre (screen-RIGHT in the front view) and ending midway through
  // the gap to the casing bottom. Built as a cubic-bezier S-tube lying in the
  // plane of the discharge face, hugging it.
  const conduitRadius     = fromMM(10); // 20mm pipe - thin, as the drawing shows
  const conduitFaceOffset = fromMM(10); // pipe centre proud of the face (= radius, so it touches it)
  const conduitExitEmbed  = fromMM(0); // centerline starts exactly on the terminal box underside
  // The terminal box is near the outer edge of the discharge face. The conduit
  // turns inward across that face and lands on the casing bottom, at the seam
  // between the terminal box and the main casing (as in the reference view).
  const conduitSweep      = fromMM(70);
  const conduitExitRise   = fromMM(10);
  const conduitLandRun    = fromMM(35);
  const conduitCurve = useMemo(
    () => {
      const faceX = boxLength / 2 + conduitFaceOffset;
      const exitY = -boxHeight / 2 + terminalBoxBottomUp + conduitExitEmbed; // under the terminal box's mid
      const exitZ = terminalBoxZ;
      // Terminate halfway through the gap between the terminal box underside
      // and casing bottom, rather than dropping to the casing's lowest edge.
      const casingBottomY = -boxHeight / 2;
      const landY = casingBottomY + terminalBoxBottomUp / 2;
      // Positive Z projects toward the casing centre in the end elevation.
      // The reference pipe curves inward from the terminal box before it
      // reaches the casing's lower edge.
      const landZ = exitZ + conduitSweep;
      return new THREE.CubicBezierCurve3(
        new THREE.Vector3(faceX, exitY, exitZ),
        new THREE.Vector3(faceX, exitY - conduitExitRise, exitZ),
        // Approach along the face toward the casing centre, so the end of the
        // conduit doesn't hook down into the base rail.
        new THREE.Vector3(faceX, landY, landZ - conduitLandRun),
        new THREE.Vector3(faceX, landY, landZ),
      );
    },
    [boxLength, boxHeight, terminalBoxBottomUp, terminalBoxZ, conduitFaceOffset, conduitExitEmbed, conduitSweep, conduitExitRise, conduitLandRun],
  );
  const conduitGeom = useMemo(
    () => new THREE.TubeGeometry(conduitCurve, 32, conduitRadius, 12, false),
    [conduitCurve, conduitRadius],
  );

  // Flexible connector between the cone and the box inlet: a flange, the
  // flexible sleeve itself, then a second flange - 50 + 100 + 50 = 200mm
  // overall. Not dimensioned in the selector, so the three lengths are fixed
  // here (the same way PRL-TC hardcodes its 75/100/75 stack in PRLTCFan.js).
  // It is always the square inlet cross-section, since it lands on the box's
  // inlet cutout at one end and the cone's narrow end at the other.
  const FLEX_FLANGE_MM = 50;
  const FLEX_SLEEVE_MM = 100;
  const flexFlangeLength = fromMM(FLEX_FLANGE_MM);
  const flexSleeveLength = fromMM(FLEX_SLEEVE_MM);
  const flexibleConnectorLength = fromMM(FLEX_FLANGE_MM * 2 + FLEX_SLEEVE_MM);

  // The exterior fan frame sits at the RIGHT end of the unit, so the AHU body
  // ends at this frame's left edge. The connection assembly runs rightward
  // from that wall as one continuous, gap-free chain:
  //   frame wall > outlet flange (50) > cone > flexible connector (200) >
  //   box cutout
  // coneGap is the length of that WHOLE chain, exactly as the client drawing
  // dimensions its 500 across the flange, cone and connector together - not
  // the cone alone. So the end pieces come out of the gap and the cone takes
  // what is left, keeping the box where the backend sized the frame for
  // (frame.length = coneGap + boxLength).
  const gap = fromMM(frame.fan?.coneGap ?? 500);
  const ahuLeftWallX = -frameLength / 2;
  const boxCutoutFaceX = ahuLeftWallX + gap;
  const boxCenterX = boxCutoutFaceX + boxLength / 2;
  // Outlet flange: the 50mm collar joining the AHU casing to the cone's wide
  // end. It comes out of the gap like the connector does, so the chain
  // measures coneGap end to end.
  const OUTLET_FLANGE_MM = 50;
  const outletFlangeLength = fromMM(OUTLET_FLANGE_MM);
  const outletFlangeSpan = outletFlangeLength;
  // Fixed lengths, never scaled: the flange is 50 and the connector 50 + 100 +
  // 50, as the client drawing shows. The shortest BOX-T-HT gap in the
  // ConeLengths table is 500 against 250 of end pieces, and the backend floors
  // ConeDistance at 500, so there is no case where the cone has to shrink away
  // to nothing.
  const flexConnectorLength = flexibleConnectorLength;
  const flexConnectorStartX = boxCutoutFaceX - flexConnectorLength;
  // Cone starts at the flange's DOWNSTREAM face, not at the frame wall, so it
  // lands on the flange the same way it lands on the connector at the far end.
  const coneStartX = ahuLeftWallX + outletFlangeSpan;
  // Vertical placement. The box does NOT hang off the frame centre - it stands
  // on the floor, on its mounting bars, on a base frame. Working up from the
  // ground (same reference PRLTCFan.js uses for its floor mounting frame):
  //
  //     ground  = -frameHeight/2 - ahuBaseFrameHeight (+frameThickness to
  //               cancel this group's own -frameThickness offset)
  //     mounting bars sit on the ground, barHeight tall
  //     base frame channel stands AROUND those bars, from the ground up to
  //       the box underside, so the bars are hidden inside it and only the
  //       bolt tabs poke out at each end
  //     box bottom    sits on the top of the channel
  //
  // The channel height IS the base frame height, so the box rides directly on
  // it. The bars are the same 70mm depth, so they now sit flush inside the
  // channel rather than being over-topped by it.
  //
  // This group is drawn at y = -frameThickness, so everything below is in
  // group-local coordinates (add frameThickness back).
  const ahuBaseFrameHeight = fromMM(frame.baseFrame?.height || 0);
  // The ground line must match where Frame.js actually puts the AHU base
  // frame's underside, or the fan floats. Frame.js centres that base frame at
  //     -(frameHeight/2 + frameThickness + baseFrameHeight/2) - 0.005
  // so its underside - the real floor - is one more half-height below:
  //     -frameHeight/2 - frameThickness - ahuBaseFrameHeight - 0.005
  // This group is itself drawn at y = -frameThickness, so add that back to get
  // the group-local figure. Deriving it this way rather than guessing the
  // stack keeps the fan on the floor whatever the frame thickness is; the old
  // form was short by frameThickness + 5mm and rose with thicker panels.
  // (the group's own -frameThickness offset cancels the one in that stack)
  const BASE_FRAME_GAP = 0.005; // Frame.js nudge, kept in step
  const groundY = -frameHeight / 2 - ahuBaseFrameHeight - BASE_FRAME_GAP;
  // Either way the box stands ON THE FLOOR - it is an exterior unit sitting on
  // the same ground as the AHU. With a base frame it rides on the channel;
  // without one it stands on its four corner feet. The old fallback put the
  // box at the frame centre (y = 0), which only looked right when boxHeight +
  // footSize happened to match the frame, and otherwise left it floating above
  // the floor or sunk through it.
  const boxCenterY = hasBaseBar
    ? groundY + baseFrameHeight + boxHeight / 2
    : groundY + footSize + boxHeight / 2;
  const boxCenterZ = 0;

  // TEMP DIAGNOSTIC - remove once the ground line is confirmed.
  // Frame.js puts the AHU base frame's UNDERSIDE at
  //   -(height/2 + frameThickness + baseFrameHeight/2) - 0.005 - baseFrameHeight/2
  // which is the true ground. Compare that against what this component uses.
  if (typeof window !== "undefined" && window.__BOXFAN_DEBUG) {
    const toMM = (v) => Math.round(v * 10000) / 10;
    const trueGroundWorld =
      -(frameHeight / 2 + frameThickness + ahuBaseFrameHeight / 2) -
      0.005 -
      ahuBaseFrameHeight / 2;
    const boxFanGroundWorld = groundY - frameThickness;
    /* eslint-disable no-console */
    console.log("[BoxFan]", frame.id, {
      frameHeight: toMM(frameHeight),
      frameThickness: toMM(frameThickness),
      ahuBaseFrameHeight: toMM(ahuBaseFrameHeight),
      baseFrameVisible: frame.baseFrame?.visible,
      hasBaseBar,
      mountingBarPitch: toMM(mountingBarPitch),
      mountingBarHoleSpacing: toMM(mountingBarHoleSpacing),
      mountingBarLength: toMM(mountingBarLength),
      baseFrameHeight: toMM(baseFrameHeight),
      boxHeight: toMM(boxHeight),
      groundY_local: toMM(groundY),
      boxFanGround_world: toMM(boxFanGroundWorld),
      trueGround_world: toMM(trueGroundWorld),
      ERROR_mm: toMM(boxFanGroundWorld - trueGroundWorld),
    });
    /* eslint-enable no-console */
  }

  // Inlet centre on the AHU-facing box face (in world-frame coords)
  const inletCenterY = boxCenterY;
  const inletCenterZ = boxCenterZ;

  // The cone runs from the AHU wall opening onto the flexible connector, which
  // in turn lands on the box cutout.
  //  - X: the wide end sits on the frame's left bound. AHUDesigner pulls this
  //    exterior frame back by one frame thickness (it draws no corner posts of
  //    its own), so that bound already coincides with the AHU body's right
  //    bound — no extra extension is needed, and the joint stays gap-free in
  //    both the geometry and the dimension chain. The narrow end stops short of
  //    the box by the connector's 200mm.
  //  - Y: this whole group is drawn at y = -frameThickness (so the box sits on
  //    the floor), which would drop the wide end below the opening — add
  //    frameThickness back on the wide end only, leaving the narrow end on the
  //    connector.
  const coneWideX = coneStartX;
  const coneNarrowX = flexConnectorStartX;
  const coneWideTopY = frameHeight / 2 + frameThickness;
  const coneWideBottomY = -frameHeight / 2 + frameThickness;

  // Outlet flange geometry: spans the wall to the cone's wide end, so the cone
  // starts ON the flange rather than on the bare frame. Sized to the full frame
  // opening (the cone's wide cross-section) and stood slightly proud, so it
  // reads as the collar it is.
  // Bore must equal the frame opening, not exceed it - see ESP.js. Sizing the
  // outer face to frame + proud and then hollowing it by a thin wall left a
  // bore wider than the cone, so the cone slid through and hid the flange.
  const outletFlangeProud = fromMM(15);
  const outletFlangeWallThickness = outletFlangeProud;
  // Centre of the flange's own span, carrying the same +frameThickness lift
  // the cone's wide end has (this group is drawn at y = -frameThickness), so
  // the two stay concentric.
  const outletFlangeX = ahuLeftWallX + outletFlangeSpan / 2;
  const outletFlangeY = (coneWideTopY + coneWideBottomY) / 2;
  const outletFlangeGeom = useMemo(
    () =>
      buildHollowFrame(
        outletFlangeSpan,
        frameHeight + outletFlangeProud * 2,
        frameWidth + outletFlangeProud * 2,
        outletFlangeWallThickness,
      ),
    [outletFlangeSpan, frameHeight, frameWidth, outletFlangeProud, outletFlangeWallThickness],
  );

  // Cone from the AHU frame opening (full frame cross-section, as PRLTCFan
  // does) down to the box's inlet face, landing exactly on both ends.
  // buildESPConeGeometry mirrors X (section[0].x *= -1, section[3].x *= -1)
  // before building, so pass pre-negated X — same trick as PRLTCFan's
  // leftPositionX — so the cone actually lands at coneWideX/coneNarrowX.
  const coneGeom = useMemo(() => {
    return buildESPConeGeometry([
      // top
      [
        new THREE.Vector3(-coneWideX, coneWideTopY, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(-coneNarrowX, inletCenterY + inletSize / 2, inletCenterZ - inletSize / 2),
        inletSize,
        0,
      ],
      // bottom
      [
        new THREE.Vector3(-coneWideX, coneWideBottomY, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(-coneNarrowX, inletCenterY - inletSize / 2, inletCenterZ - inletSize / 2),
        inletSize,
        0,
      ],
      // back
      [
        new THREE.Vector3(-coneWideX, coneWideTopY, -frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(-coneNarrowX, inletCenterY + inletSize / 2, inletCenterZ - inletSize / 2),
        0,
        -inletSize,
      ],
      // front
      [
        new THREE.Vector3(-coneWideX, coneWideTopY, frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(-coneNarrowX, inletCenterY + inletSize / 2, inletCenterZ + inletSize / 2),
        0,
        -inletSize,
      ],
    ]);
  }, [coneWideX, coneNarrowX, coneWideTopY, coneWideBottomY, frameHeight, frameWidth, inletCenterY, inletCenterZ, inletSize]);

  // Flexible connector geometry: three hollow square tubes on the inlet
  // cross-section, butted end to end between the cone's narrow end and the box
  // inlet. The two flanges stand slightly proud of the sleeve so the joint
  // reads as the stepped lip it is on the drawing, matching the way the sleeve
  // is drawn narrower than its flanges on PRL-TC.
  //   buildHollowFrame(width, height, depth, thickness) extrudes along X, with
  //   height on Y and depth on Z, centred on the origin.
  const flexFlangeProud = fromMM(30);
  const flexWallThickness = fromMM(6);

  // Segment spans along X, working back from the box inlet face. The geometry
  // is built from these same spans, so the drawn tubes always match the
  // positions they are placed at.
  const flexFlangeSpan = flexFlangeLength;
  const flexSleeveSpan = flexSleeveLength;

  const flexFlangeGeom = useMemo(
    () =>
      buildHollowFrame(
        flexFlangeSpan,
        inletSize + flexFlangeProud,
        inletSize + flexFlangeProud,
        flexWallThickness,
      ),
    [flexFlangeSpan, inletSize, flexFlangeProud, flexWallThickness],
  );

  const flexSleeveGeom = useMemo(
    () => buildHollowFrame(flexSleeveSpan, inletSize, inletSize, flexWallThickness),
    [flexSleeveSpan, inletSize, flexWallThickness],
  );

  // Base frame channel. Only built for ranges that actually have a base frame;
  // the rest still fall back to the four corner feet.
  const baseFrameGeometry = useMemo(
    () =>
      hasBaseBar
        ? buildBaseFrameChannel(
            boxLength,
            baseFrameSpan,
            baseFrameHeight,
            channelFootInset,
            channelTaperHeight,
            channelFootInsetZ,
          )
        : null,
    [
      hasBaseBar,
      boxLength,
      baseFrameSpan,
      baseFrameHeight,
      channelFootInset,
      channelTaperHeight,
      channelFootInsetZ,
    ],
  );

  const flexOutletFlangeX = boxCutoutFaceX - flexFlangeSpan / 2;
  const flexSleeveX = boxCutoutFaceX - flexFlangeSpan - flexSleeveSpan / 2;
  const flexInletFlangeX = boxCutoutFaceX - flexFlangeSpan - flexSleeveSpan - flexFlangeSpan / 2;

  // End-face plate: the full box cross-section with the connector opening cut
  // out of the middle. Shape X maps to the box width (world Z) and shape Y to
  // the box height once the plate is rotated onto the face.
  const buildFaceShape = (holeWidth, holeHeight) => {
    const shape = new THREE.Shape();
    shape.moveTo(-boxWidth / 2, -boxHeight / 2);
    shape.lineTo(boxWidth / 2, -boxHeight / 2);
    shape.lineTo(boxWidth / 2, boxHeight / 2);
    shape.lineTo(-boxWidth / 2, boxHeight / 2);
    shape.lineTo(-boxWidth / 2, -boxHeight / 2);

    const hole = new THREE.Path();
    const halfW = holeWidth / 2;
    const halfH = holeHeight / 2;
    hole.moveTo(-halfW, -halfH);
    hole.lineTo(halfW, -halfH);
    hole.lineTo(halfW, halfH);
    hole.lineTo(-halfW, halfH);
    hole.lineTo(-halfW, -halfH);
    shape.holes.push(hole);
    return shape;
  };

  const inletShape = useMemo(
    () => buildFaceShape(inletSize, inletSize),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boxWidth, boxHeight, inletSize],
  );

  const outletShape = useMemo(
    () => buildFaceShape(outletWidth, outletHeight),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boxWidth, boxHeight, outletWidth, outletHeight],
  );

  // Box materials. BoxGeometry face order: [+X, -X, +Y, -Y, +Z, -Z]. -X is the
  // AHU-facing (inlet) side and +X the discharge side; both carry a connector
  // opening, so both are hidden and replaced by the cut-out plates below.
  const boxMaterials = useMemo(
    () => [
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide, transparent: true, opacity: 0 }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide, transparent: true, opacity: 0 }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
    ],
    [selected],
  );

  const feetPositions = [
    [-boxLength / 2 + cornerOffset + footSize / 2, -boxHeight / 2 - footSize / 2, -boxWidth / 2 + cornerOffset + footSize / 2],
    [-boxLength / 2 + cornerOffset + footSize / 2, -boxHeight / 2 - footSize / 2, boxWidth / 2 - cornerOffset - footSize / 2],
    [boxLength / 2 - cornerOffset - footSize / 2, -boxHeight / 2 - footSize / 2, -boxWidth / 2 + cornerOffset + footSize / 2],
    [boxLength / 2 - cornerOffset - footSize / 2, -boxHeight / 2 - footSize / 2, boxWidth / 2 - cornerOffset - footSize / 2],
  ];

  // Grill bars across each connector opening, evenly spread over the opening's
  // height. The inlet and discharge are sized independently, so each face gets
  // its own ladder.
  const grillBarYs = (openingHeight) =>
    Array.from({ length: 5 }, (_, i) => -openingHeight / 2 + (i + 0.5) * (openingHeight / 5));

  const inletGrillBarPositions = grillBarYs(inletSize).map((y) => [-boxLength / 2 - 0.01, y, 0]);
  const outletGrillBarPositions = grillBarYs(outletHeight).map((y) => [boxLength / 2 + 0.01, y, 0]);

  const frontGrillBarPositions = Array.from({ length: 6 }, (_, i) => [0, (i - 2.5) * frontBackGrillSpacing, -boxWidth / 2 + 0.01]);
  const backGrillBarPositions = Array.from({ length: 6 }, (_, i) => [0, (i - 2.5) * frontBackGrillSpacing, boxWidth / 2 - 0.01]);

  return (
    <group position={[0, -frameThickness, 0]}>
      {/* Outlet flange: 25mm collar joining the AHU casing to the cone. */}
      <mesh
        geometry={outletFlangeGeom}
        position={[outletFlangeX, outletFlangeY, 0]}
        userData={{ excludeProjections: ["left", "right"] }}
      >
        <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>

      <mesh geometry={coneGeom} userData={{ excludeProjections: ["left", "right"] }}>
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
        </Edges>
      </mesh>

      {/* Flexible connector joining the cone to the box inlet: flange (50),
          flexible sleeve (100), flange (50). Centred on the box inlet, which
          sits at the box's own height rather than the frame centre. */}
      {flexConnectorLength > 0 && (
        <>
          <mesh
            geometry={flexFlangeGeom}
            position={[flexInletFlangeX, inletCenterY, inletCenterZ]}
            userData={{ excludeProjections: ["left", "right"] }}
          >
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>

          <mesh
            geometry={flexSleeveGeom}
            position={[flexSleeveX, inletCenterY, inletCenterZ]}
            userData={{ excludeProjections: ["left", "right"] }}
          >
            <meshStandardMaterial color={selected ? "Orange" : "DimGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>

          <mesh
            geometry={flexFlangeGeom}
            position={[flexOutletFlangeX, inletCenterY, inletCenterZ]}
            userData={{ excludeProjections: ["left", "right"] }}
          >
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        </>
      )}

      <group position={[boxCenterX, boxCenterY, boxCenterZ]}>
        {/* 25mm profile frame around the box body: outer shell + inner cavity */}
        <mesh material={boxMaterials}>
          <boxGeometry args={[boxLength, boxHeight, boxWidth]} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>
        {/* Inner cavity: 25mm inset on all sides creates the profile frame */}
        <mesh>
          <boxGeometry args={[boxLength - fromMM(50), boxHeight - fromMM(50), boxWidth - fromMM(50)]} />
          <meshStandardMaterial color={selected ? "Orange" : "White"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {[-1, 1].map((sx) =>
          [-1, 1].map((sy) =>
            [-1, 1].map((sz) => (
              <group key={`casing-corner-${sx}-${sy}-${sz}`}>
                {/* step line across the top / bottom band */}
                <mesh
                  position={[
                    (sx * boxLength) / 2,
                    sy * (boxHeight / 2 - casingProfileWidth / 2),
                    sz * (boxWidth / 2 - casingProfileWidth - casingCornerStep),
                  ]}
                  userData={{ excludeProjections: ["top", "side"] }}
                >
                  <boxGeometry
                    args={[
                      casingStepLineThickness,
                      casingProfileWidth,
                      casingStepLineThickness,
                    ]}
                  />
                  <meshStandardMaterial color={selected ? "Orange" : "Grey"} />
                </mesh>
                {/* step line across the left / right band */}
                <mesh
                  position={[
                    (sx * boxLength) / 2,
                    sy * (boxHeight / 2 - casingProfileWidth - casingCornerStep),
                    sz * (boxWidth / 2 - casingProfileWidth / 2),
                  ]}
                  userData={{ excludeProjections: ["top", "side"] }}
                >
                  <boxGeometry
                    args={[
                      casingStepLineThickness,
                      casingStepLineThickness,
                      casingProfileWidth,
                    ]}
                  />
                  <meshStandardMaterial color={selected ? "Orange" : "Grey"} />
                </mesh>
              </group>
            )),
          ),
        )}

        {/* Inlet (-X) connector: the square opening the cone lands on. */}
        <mesh position={[-boxLength / 2 - 0.01, 0, 0]} rotation={[0, Math.PI / 2, 0]} userData={{ excludeProjections: ["right"] }}>
          <extrudeGeometry args={[inletShape, { depth: grillBarThickness, bevelEnabled: false }]} />
          <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {inletGrillBarPositions.map((p, i) => (
          <mesh key={`inlet-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]} userData={{ excludeProjections: ["right"] }}>
            <boxGeometry args={[cutoutGrillBarWidth, inletSize, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {/* Discharge (+X) connector: OutletHeight x OutletWidth. The hole is
            centred on the plate's X, so mirroring the plate with a -90° Y
            rotation extrudes it inward from the far face without flipping the
            opening off-centre. */}
        <mesh position={[boxLength / 2 + 0.01, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
          <extrudeGeometry args={[outletShape, { depth: grillBarThickness, bevelEnabled: false }]} />
          <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {outletGrillBarPositions.map((p, i) => (
          <mesh key={`outlet-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]} userData={{ excludeProjections: ["right"] }}>
            <boxGeometry args={[cutoutGrillBarWidth, outletWidth, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {/* Louvres below the outlet - horizontal dash segments for dashed appearance in Front View */}
        {louvreBarPositions.map((p, i) => (
          <mesh key={`louvre-${i}`} position={p}>
            <boxGeometry args={[louvreDashLength, louvreDashThickness, louvreDashWidth]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {frontGrillBarPositions.map((p, i) => (
          <mesh key={`front-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]} userData={{ excludeProjections: ["right"] }}>
            <boxGeometry args={[frontBackGrillBarWidth, frontBackGrillBarLength, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {backGrillBarPositions.map((p, i) => (
          <mesh key={`back-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]} userData={{ excludeProjections: ["right"] }}>
            <boxGeometry args={[frontBackGrillBarWidth, frontBackGrillBarLength, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {/* Terminal box on the discharge face, bottom-front, with the flexible
            wiring conduit drooping from its underside to the box bottom - as
            the client drawing shows. Deliberately NOT excluded from any
            projection: the end views (left / right) must show them face-on
            and the side view as the protrusion at the discharge end. */}
        <mesh position={[terminalBoxX, terminalBoxY, terminalBoxZ]}>
          <boxGeometry args={[terminalBoxProtrusion, terminalBoxHeight, terminalBoxWidth]} />
          <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {/* The conduit curve is already in box-local coordinates, so the mesh
            sits at the group origin. No <Edges>: it reads as a smooth pipe in
            3D, and the projected drawings pick up its curved silhouette from
            the merged-geometry edge pass (same as they did for the pipe). */}
        <mesh geometry={conduitGeom}>
          <meshStandardMaterial color={selected ? "Orange" : "DimGrey"} metalness={0.4} roughness={0.5} />
        </mesh>
        {/* Rounded terminal on the free end: TubeGeometry leaves its ends open,
            while the reference drawing shows a small closed circular tip. */}
        <mesh position={conduitCurve.getPointAt(1)}>
          <sphereGeometry args={[conduitRadius, 12, 8]} />
          <meshStandardMaterial color={selected ? "Orange" : "DimGrey"} metalness={0.4} roughness={0.5} />
        </mesh>

        {/* Base assembly, built up from the ground:
              two mounting bars run ACROSS the fan at pitch A, sitting ON the
                floor, with a fixing hole near each end. They stop just inside
                the channel so they never show a square corner at the base.
              the base frame is a perimeter CHANNEL standing around those bars
                - not a tray on top of them - spanning the mounting bars (F) so
                its ends are the outer corner of the base and the bend of the
                chamfer lands there, not in some internal place.
                It is widest at the top, under the box, and drawn in at the
                floor along both axes, so the front and side elevations each
                show the slanted bottom corners of the client drawing.
              the box sits on top of the channel.
            Falls back to the four corner feet when a range has no base frame. */}
        {hasBaseBar ? (
          <group position={[0, groundY - boxCenterY, 0]}>
            {/* Mounting bars, on the ground */}
            {[-mountingBarPitch / 2, mountingBarPitch / 2].map((x, i) => (
              <group key={`baseframe-bar-${i}`} position={[x, barHeight / 2, 0]}>
                {/* Kept out of the front and back drawings as well: the channel
                    wraps the bars, so all their edges did there was add lines
                    around the fixing points. */}
                <mesh userData={{ excludeProjections: ["left", "right"] }}>
                  <boxGeometry args={[barThickness, barHeight, barSpan]} />
                  <meshStandardMaterial color="DimGrey" metalness={0.6} roughness={0.5} />
                  <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Grey" widthTest={true} />
                  </Edges>
                </mesh>
                {/* Fixing hole near each end of the bar. Kept out of the front
                    and back drawings: projected edge-on, the cylinder reads as
                    a boxy rectangle on the sheet. */}
                {[-1, 1].map((s) => (
                  <mesh
                    key={`bar-hole-${s}`}
                    position={[0, 0, s * holeZ]}
                    userData={{ excludeProjections: ["left", "right"] }}
                  >
                    <cylinderGeometry args={[holeRadius, holeRadius, barHeight * 1.2, 16]} />
                    <meshStandardMaterial color="Black" />
                  </mesh>
                ))}
              </group>
            ))}

            {/* One plain vertical line on each side of the base, carried
                straight down from the casing edge so the sheet shows a single
                line below the fan line and nothing else. Only in the front and
                back drawings; thin along Z so its two silhouettes merge into
                one line, and slightly undersized so it cannot fight the faces
                of whatever it passes through in 3D. */}
            {[-1, 1].map((s) => (
              <mesh
                key={`base-frame-line-${s}`}
                position={[0, barHeight / 2, (s * boxWidth) / 2]}
                userData={{ excludeProjections: ["top", "side"] }}
              >
                <boxGeometry args={[barThickness * 0.98, barHeight * 0.98, holeLineThickness]} />
                <meshStandardMaterial color="DimGrey" metalness={0.6} roughness={0.5} />
              </mesh>
            ))}

            {/* Base frame channel, standing AROUND the bars and reaching from
                the ground to the box underside, so each bar is covered from
                the side. Its ends are the base's outer corner, so the chamfer
                reads as a bend of the base itself. Geometry is built with its
                top face at y = 0, hence positioning by the box underside. */}
            <mesh
              position={[0, baseFrameHeight, 0]}
              geometry={baseFrameGeometry}
            >
              <meshStandardMaterial color="Silver" metalness={0.5} roughness={0.6} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="Grey" widthTest={true} />
              </Edges>
            </mesh>
          </group>
        ) : (
          feetPositions.map((p, i) => (
          <mesh key={`foot-${i}`} position={p}>
            <boxGeometry args={[footSize, footSize, footSize]} />
            <meshStandardMaterial color="Black" />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))
        )}
      </group>
    </group>
  );
}
